import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Check,
  CheckCircle2,
  Loader2,
  Pencil,
  ThumbsDown,
  ThumbsUp,
  X,
  XCircle,
} from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useMatter } from '@/hooks/useMatter';
import type {
  FilevineSyncActionStatus,
  FilevineSyncPlanListSchema,
} from '@/services/api/generated/filevineSyncService';
import { enhancedApi } from '@/services/api/generated/filevineSyncService';

interface FilevineSyncActionItemProps {
  action: NonNullable<FilevineSyncPlanListSchema['actions']>[0];
  isStale: boolean;
}

const getStatusBadge = (status: FilevineSyncActionStatus) => {
  switch (status) {
    case 'EXECUTING':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 rounded">
          <Loader2 className="h-3 w-3 animate-spin" />
          Executing
        </span>
      );
    case 'APPROVED':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 rounded">
          <CheckCircle2 className="h-3 w-3" />
          Approved
        </span>
      );
    case 'FAILED':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-50 text-red-700 rounded">
          <XCircle className="h-3 w-3" />
          Failed
        </span>
      );
    default:
      return null;
  }
};

export const FilevineSyncActionItem: React.FC<FilevineSyncActionItemProps> = ({
  action,
  isStale,
}) => {
  const { matterId } = useMatter();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Type guard to check if action has file_name property
  const hasFileName = (
    act: typeof action,
  ): act is
    | (typeof action & { action_type: 'CREATE_BLOB'; file_name: string })
    | (typeof action & {
        action_type: 'CREATE_FILEVINE_DOCUMENT';
        file_name: string;
      }) => {
    return (
      act.action_type === 'CREATE_BLOB' ||
      act.action_type === 'CREATE_FILEVINE_DOCUMENT'
    );
  };

  const [editedFileName, setEditedFileName] = useState(
    hasFileName(action) ? action.file_name : '',
  );

  const [updateStatus] =
    enhancedApi.useFilevineSyncActionUpdateStatusMutation();
  const [updateFilename] =
    enhancedApi.useFilevineSyncActionUpdateFilenameMutation();

  const handleStatusChange = async (newStatus: FilevineSyncActionStatus) => {
    setIsUpdating(true);
    try {
      await updateStatus({
        matterId,
        actionId: action.id,
        patchedUpdateActionStatusSchema: { status: newStatus },
      }).unwrap();
      toast.success(`Action ${newStatus.toLowerCase()}`);
    } catch (error) {
      toast.error(`Failed to ${newStatus.toLowerCase()} action`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSaveFilename = async () => {
    if (!editedFileName.trim()) {
      toast.error('Filename cannot be empty');
      return;
    }

    setIsUpdating(true);
    try {
      await updateFilename({
        matterId,
        actionId: action.id,
        patchedUpdateActionFilenameSchema: { file_name: editedFileName },
      }).unwrap();
      toast.success('Filename updated');
      setIsEditing(false);
    } catch (error) {
      toast.error('Failed to update filename');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedFileName(hasFileName(action) ? action.file_name : '');
    setIsEditing(false);
  };

  const isCreateBlob = action.action_type === 'CREATE_BLOB';
  const fileName = hasFileName(action) ? action.file_name : '';
  const canEditFilename =
    !isStale &&
    !isCreateBlob &&
    hasFileName(action) &&
    (action.status === 'PENDING' || action.status === 'AWAITING_APPROVAL');

  return (
    <div className="group px-4 py-3 flex items-center justify-between hover:bg-gray-50">
      {/* File info */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {/* Direction icon */}
        <div className="flex-shrink-0">
          {isCreateBlob ? (
            <ArrowDownToLine className="h-4 w-4 text-blue-600" />
          ) : (
            <ArrowUpFromLine className="h-4 w-4 text-green-600" />
          )}
        </div>

        {/* Filename */}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Input
                value={editedFileName}
                onChange={(e) => setEditedFileName(e.target.value)}
                className="h-8 text-sm"
                disabled={isUpdating}
                autoFocus
              />
              <Button
                onClick={handleSaveFilename}
                disabled={isUpdating}
                variant="ghost"
                size="sm"
                className="h-8 px-2"
              >
                <Check className="h-4 w-4 text-green-600" />
              </Button>
              <Button
                onClick={handleCancelEdit}
                disabled={isUpdating}
                variant="ghost"
                size="sm"
                className="h-8 px-2"
              >
                <X className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {fileName}
                  </p>
                  {getStatusBadge(action.status)}
                </div>
                <p className="text-xs text-gray-500">
                  {isCreateBlob ? 'Download to Finch' : 'Upload to Filevine'}
                </p>
              </div>
              {canEditFilename && (
                <Button
                  onClick={() => setIsEditing(true)}
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Pencil className="h-3.5 w-3.5 text-gray-500" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      {action.status === 'AWAITING_APPROVAL' && (
        <div className="flex gap-2 ml-4">
          <Button
            onClick={() => handleStatusChange('APPROVED')}
            disabled={isUpdating || isStale}
            variant="ghost"
            size="sm"
            className="h-8 px-3"
          >
            <ThumbsUp className="h-3.5 w-3.5 mr-1" />
            Approve
          </Button>
          <Button
            onClick={() => handleStatusChange('REJECTED')}
            disabled={isUpdating || isStale}
            variant="ghost"
            size="sm"
            className="h-8 px-3 text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <ThumbsDown className="h-3.5 w-3.5 mr-1" />
            Reject
          </Button>
        </div>
      )}
    </div>
  );
};
